import React, { Suspense } from "react";
import { Toaster } from "sonner";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import LoadingSpinner from "./components/ui/LoadingSpinner";
import { HelmetProvider } from "react-helmet-async";
import lazyWithRetry from "./lib/utils/lazyWithRetry";

import {
  ADMIN_ROLES,
  TRAINER_ROLES,
  CANDIDATE_ROLES,
  ALL_APP_ROLES,
  COURSE_ROUTE_PERMISSIONS,
} from "./lib/utils/constants";


const Login = lazyWithRetry(() => import("./pages/auth/Login"));
const Register = lazyWithRetry(() => import("./pages/auth/Register"));
// SuperAdminDashboard import removed - /demo route removed for security
// const TrainerDashboard = lazy(
//   () => import("./pages/dashboard/TrainerDashboard"),
// );
// const CandidateDashboard = lazy(
//   () => import("./pages/dashboard/CandidateDashboard"),
// );
const CandidateList = lazyWithRetry(() => import("./pages/candidates/CandidateList"));
const AllCandidateList = lazyWithRetry(() => import("./pages/candidates/AllCandidateList"));
const CreateTrainer = lazyWithRetry(() => import("./pages/trainers/CreateTrainer"));
const EditTrainer = lazyWithRetry(() => import("./pages/trainers/EditTrainer"));
const TrainerList = lazyWithRetry(() => import("./pages/trainers/TrainerList"));
const AddCandidate = lazyWithRetry(() => import("./pages/candidates/AddCandidate"));
const EditCandidate = lazyWithRetry(() => import("./pages/candidates/EditCandidate"));
const ResetPassword = lazyWithRetry(() => import("./pages/auth/ResetPassword"));
const RolePermission = lazyWithRetry(() => import("./pages/admin/RolePermission"));
const LogHistory = lazyWithRetry(() => import("./pages/admin/LogHistory"));
const AdminNotifications = lazyWithRetry(
  () => import("./pages/admin/AdminNotifications"),
);
const AdminUserList = lazyWithRetry(() => import("./pages/admin/users/AdminUserList"));
const CreateAdminUser = lazyWithRetry(
  () => import("./pages/admin/users/CreateAdminUser"),
);
const EditAdminUser = lazyWithRetry(() => import("./pages/admin/users/EditAdminUser"));
const HotelList = lazyWithRetry(() => import("./pages/hotels/HotelList"));
const CreateHotel = lazyWithRetry(() => import("./pages/hotels/CreateHotel"));
const EditHotel = lazyWithRetry(() => import("./pages/hotels/EditHotel"));
const AdminRolesList = lazyWithRetry(
  () => import("./pages/admin/admin-roles/AdminRolesList"),
);
const AdminRolesForm = lazyWithRetry(
  () => import("./pages/admin/admin-roles/AdminRolesForm"),
);

const CertificateList = lazyWithRetry(
  () => import("./pages/certificates/CertificateList"),
);
const CreateCertificate = lazyWithRetry(
  () => import("./pages/certificates/CreateCertificate"),
);
const EditCertificate = lazyWithRetry(
  () => import("./pages/certificates/EditCertificate"),
);
const CertificatePrintView = lazyWithRetry(
  () => import("./pages/certificates/CertificatePrintView"),
);
const CertificateVerification = lazyWithRetry(
  () => import("./pages/certificates/CertificateVerification"),
);

const MasterCourseList = lazyWithRetry(() => import("./pages/courses/MasterCourseList"));
const MasterCourseForm = lazyWithRetry(() => import("./pages/courses/MasterCourseForm"));

const ActiveCourseList = lazyWithRetry(() => import("./pages/courses/ActiveCourseList"));
const ActiveCourseForm = lazyWithRetry(() => import("./pages/courses/ActiveCourseForm"));
const OuthouseCourseList = lazyWithRetry(
  () => import("./pages/outhouse-courses/OuthouseCourseList"),
);
const OuthouseCourseForm = lazyWithRetry(
  () => import("./pages/outhouse-courses/OuthouseCourseForm"),
);

const PreActiveCourseList = lazyWithRetry(
  () => import("./pages/pre-active-courses/PreActiveCourseList"),
);
const PreActiveCourseForm = lazyWithRetry(
  () => import("./pages/pre-active-courses/PreActiveCourseForm"),
);
const AdminPreActiveApprovals = lazyWithRetry(
  () => import("./pages/pre-active-courses/AdminPreActiveApprovals"),
);
const RejectedCandidateApprovals = lazyWithRetry(
  () => import("./pages/pre-active-courses/RejectedCandidateApprovals"),
);
const NominatorPortal = lazyWithRetry(
  () => import("./pages/pre-active-courses/NominatorPortal"),
);
const CandidateApprovalPortal = lazyWithRetry(
  () => import("./pages/pre-active-courses/CandidateApprovalPortal"),
);

const Acknowledge = lazyWithRetry(() => import("./pages/Acknowledge"));

const UnifiedDashboard = lazyWithRetry(
  () => import("./pages/dashboard/UnifiedDashboard"),
);

const ReportDashboard = lazyWithRetry(() => import("./pages/reports/ReportDashboard"));
const HotelReport = lazyWithRetry(() => import("./pages/reports/HotelReport"));
const AdminRemarksReport = lazyWithRetry(
  () => import("./pages/reports/AdminRemarksReport"),
);

const LocationList = lazyWithRetry(() => import("./pages/locations/LocationList"));
const CreateLocation = lazyWithRetry(() => import("./pages/locations/CreateLocation"));
const EditLocation = lazyWithRetry(() => import("./pages/locations/EditLocation"));

const SystemManualList = lazyWithRetry(
  () => import("./pages/system-manual/SystemManualList"),
);
const CreateSystemManual = lazyWithRetry(
  () => import("./pages/system-manual/CreateSystemManual"),
);
const EditSystemManual = lazyWithRetry(
  () => import("./pages/system-manual/EditSystemManual"),
);
const SystemManualCategoryList = lazyWithRetry(
  () => import("./pages/system-manual/SystemManualCategoryList"),
);

const StudyMaterialList = lazyWithRetry(
  () => import("./pages/study-material/StudyMaterialList"),
);
const CreateStudyMaterial = lazyWithRetry(
  () => import("./pages/study-material/CreateStudyMaterial"),
);
const EditStudyMaterial = lazyWithRetry(
  () => import("./pages/study-material/EditStudyMaterial"),
);

const NominatorList = lazyWithRetry(() => import("./pages/nominators/NominatorList"));
const CreateNominator = lazyWithRetry(
  () => import("./pages/nominators/CreateNominator"),
);
const EditNominator = lazyWithRetry(() => import("./pages/nominators/EditNominator"));

const PrivateRoute = lazyWithRetry(() => import("./components/routes/PrivateRoute"));
const PublicRoute = lazyWithRetry(() => import("./components/routes/PublicRoute"));

const FeedbackCategoryList = lazyWithRetry(
  () => import("./pages/feedback/FeedbackCategoryList"),
);
const FeedbackQuestionList = lazyWithRetry(
  () => import("./pages/feedback/FeedbackQuestionList"),
);
// Remove SubmittedFeedbackList import as it is obsolete now
const SubmittedFeedbackDetails = lazyWithRetry(
  () => import("./pages/feedback/SubmittedFeedbackDetails"),
);
const FeedbackFormList = lazyWithRetry(
  () => import("./pages/feedback/FeedbackFormList"),
);
const FeedbackFormCreate = lazyWithRetry(
  () => import("./pages/feedback/FeedbackFormCreate"),
);

const QuestionBankList = lazyWithRetry(
  () => import("./pages/assessment/QuestionBankList"),
);
const QuestionBankForm = lazyWithRetry(
  () => import("./pages/assessment/QuestionBankForm"),
);

const AssessmentList = lazyWithRetry(() => import("./pages/assessment/AssessmentList"));
const AssessmentForm = lazyWithRetry(() => import("./pages/assessment/AssessmentForm"));

const SubmittedAssessmentList = lazyWithRetry(
  () => import("./pages/assessment/SubmittedAssessmentList"),
);
const CourseSubmissions = lazyWithRetry(
  () => import("./pages/assessment/CourseSubmissions"),
);
// const AssessmentSubmissionList = lazy(
//   () => import("./pages/assessment/AssessmentSubmissionList"),
// );
const SubmissionDetail = lazyWithRetry(
  () => import("./pages/assessment/SubmissionDetail"),
);

const TrainerCertificateList = lazyWithRetry(
  () => import("./pages/trainers/TrainerCertificateList"),
);
const TrainerCourseList = lazyWithRetry(
  () => import("./pages/trainers/TrainerCourseList"),
);

const FeedbackCourseList = lazyWithRetry(
  () => import("./pages/feedback/FeedbackCourseList"),
);



const CandidateNominations = lazyWithRetry(
  () => import("./pages/candidates/CandidateNominations"),
);

const CandidateCourseList = lazyWithRetry(
  () => import("./pages/candidates/CandidateCourseList"),
);


const CandidateCertificateList = lazyWithRetry(
  () => import("./pages/candidates/CandidateCertificateList"),
);

const CandidateCourseDetails = lazyWithRetry(
  () => import("./pages/candidates/CandidateCourseDetails"),
);
const ReimbursementList = lazyWithRetry(
  () => import("./pages/reimbursements/ReimbursementList"),
);
const ReimbursementForm = lazyWithRetry(
  () => import("./pages/reimbursements/ReimbursementForm"),
);
const ReimbursementDetails = lazyWithRetry(
  () => import("./pages/reimbursements/ReimbursementDetails"),
);
const ReimbursementAdminList = lazyWithRetry(
  () => import("./pages/admin/reimbursements/ReimbursementAdminList"),
);
const ReimbursementAdminDetails = lazyWithRetry(
  () => import("./pages/admin/reimbursements/ReimbursementAdminDetails"),
);

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <Toaster position="top-center" richColors />
        <AuthProvider>
          <Suspense fallback={<LoadingSpinner />}>
            <Routes>
              <Route
                path="/login"
                element={
                  <PublicRoute>
                    <Login />
                  </PublicRoute>
                }
              />
              <Route
                path="/register"
                element={
                  <PublicRoute>
                    <Register />
                  </PublicRoute>
                }
              />
              <Route
                path="/reset-password"
                element={
                  <PublicRoute>
                    <ResetPassword />
                  </PublicRoute>
                }
              />
              <Route
                path="/authenticity-verification/:id"
                element={<CertificateVerification />}
              />

              <Route path="/nominate/:token" element={<NominatorPortal />} />
              <Route
                path="/candidate-approval/:token"
                element={<CandidateApprovalPortal />}
              />

              <Route
                path="/acknowledge"
                element={<Acknowledge />}
              />

              <Route path="/" element={<Navigate to="/login" replace />} />

              <Route
                path="/dashboard"
                element={
                  <PrivateRoute
                    allowedRoles={ALL_APP_ROLES}
                    allowRestrictedAdminWithoutPermissions={true}
                  >
                    <UnifiedDashboard />
                  </PrivateRoute>
                }
              />

              <Route
                path="/courses"
                element={
                  <PrivateRoute
                    allowedRoles={ADMIN_ROLES}
                    requiredAnyPermissions={COURSE_ROUTE_PERMISSIONS}
                  >
                    <MasterCourseList />
                  </PrivateRoute>
                }
              />
              <Route
                path="/courses/add"
                element={
                  <PrivateRoute
                    allowedRoles={ADMIN_ROLES}
                    requiredAnyPermissions={COURSE_ROUTE_PERMISSIONS}
                  >
                    <MasterCourseForm />
                  </PrivateRoute>
                }
              />
              <Route
                path="/courses/edit/:id"
                element={
                  <PrivateRoute
                    allowedRoles={ADMIN_ROLES}
                    requiredAnyPermissions={COURSE_ROUTE_PERMISSIONS}
                  >
                    <MasterCourseForm />
                  </PrivateRoute>
                }
              />

              <Route
                path="/active-courses"
                element={
                  <PrivateRoute
                    allowedRoles={ADMIN_ROLES}
                    requiredAnyPermissions={COURSE_ROUTE_PERMISSIONS}
                  >
                    <ActiveCourseList />
                  </PrivateRoute>
                }
              />
              <Route
                path="/active-courses/add"
                element={
                  <PrivateRoute
                    allowedRoles={ADMIN_ROLES}
                    requiredAnyPermissions={COURSE_ROUTE_PERMISSIONS}
                  >
                    <ActiveCourseForm />
                  </PrivateRoute>
                }
              />
              <Route
                path="/active-courses/edit/:id"
                element={
                  <PrivateRoute
                    allowedRoles={ADMIN_ROLES}
                    requiredAnyPermissions={COURSE_ROUTE_PERMISSIONS}
                  >
                    <ActiveCourseForm />
                  </PrivateRoute>
                }
              />

              <Route
                path="/outhouse-courses"
                element={
                  <PrivateRoute
                    allowedRoles={ADMIN_ROLES}
                    requiredAnyPermissions={COURSE_ROUTE_PERMISSIONS}
                  >
                    <OuthouseCourseList />
                  </PrivateRoute>
                }
              />
              <Route
                path="/outhouse-courses/add"
                element={
                  <PrivateRoute
                    allowedRoles={ADMIN_ROLES}
                    requiredAnyPermissions={COURSE_ROUTE_PERMISSIONS}
                  >
                    <OuthouseCourseForm />
                  </PrivateRoute>
                }
              />
              <Route
                path="/outhouse-courses/edit/:id"
                element={
                  <PrivateRoute
                    allowedRoles={ADMIN_ROLES}
                    requiredAnyPermissions={COURSE_ROUTE_PERMISSIONS}
                  >
                    <OuthouseCourseForm />
                  </PrivateRoute>
                }
              />

              <Route
                path="/pre-active-courses"
                element={
                  <PrivateRoute
                    allowedRoles={ADMIN_ROLES}
                    requiredAnyPermissions={COURSE_ROUTE_PERMISSIONS}
                  >
                    <PreActiveCourseList />
                  </PrivateRoute>
                }
              />
              <Route
                path="/pre-active-courses/add"
                element={
                  <PrivateRoute
                    allowedRoles={ADMIN_ROLES}
                    requiredAnyPermissions={COURSE_ROUTE_PERMISSIONS}
                  >
                    <PreActiveCourseForm />
                  </PrivateRoute>
                }
              />
              <Route
                path="/pre-active-courses/edit/:id"
                element={
                  <PrivateRoute
                    allowedRoles={ADMIN_ROLES}
                    requiredAnyPermissions={COURSE_ROUTE_PERMISSIONS}
                  >
                    <PreActiveCourseForm />
                  </PrivateRoute>
                }
              />
              <Route
                path="/pre-active-courses/rejected-approvals"
                element={
                  <PrivateRoute
                    allowedRoles={ADMIN_ROLES}
                    requiredPermission="view_pre_active_approvals"
                  >
                    <RejectedCandidateApprovals />
                  </PrivateRoute>
                }
              />
              <Route
                path="/pre-active-courses/:id/approvals"
                element={
                  <PrivateRoute
                    allowedRoles={ADMIN_ROLES}
                    requiredPermission="view_pre_active_approvals"
                  >
                    <AdminPreActiveApprovals />
                  </PrivateRoute>
                }
              />

              <Route
                path="/dashboard/super-admin"
                element={
                  <PrivateRoute
                    allowedRoles={ADMIN_ROLES}
                    allowRestrictedAdminWithoutPermissions={true}
                  >
                    <UnifiedDashboard />
                  </PrivateRoute>
                }
              />

              <Route
                path="/trainers"
                element={
                  <PrivateRoute
                    allowedRoles={ADMIN_ROLES}
                    requiredPermission="view_trainers"
                  >
                    <TrainerList />
                  </PrivateRoute>
                }
              />

              <Route
                path="/trainer/create"
                element={
                  <PrivateRoute
                    allowedRoles={ADMIN_ROLES}
                    requiredPermission="create_trainer"
                  >
                    <CreateTrainer />
                  </PrivateRoute>
                }
              />

              <Route
                path="/trainer/edit/:id"
                element={
                  <PrivateRoute
                    allowedRoles={ADMIN_ROLES}
                    requiredPermission="edit_trainer"
                  >
                    <EditTrainer />
                  </PrivateRoute>
                }
              />

              <Route
                path="/candidates/molmi"
                element={
                  <PrivateRoute
                    allowedRoles={ADMIN_ROLES}
                    requiredPermission="view_candidates"
                  >
                    <CandidateList key="molmi" registrationType="MOLMI Employee" />
                  </PrivateRoute>
                }
              />
              <Route
                path="/candidates/others"
                element={
                  <PrivateRoute
                    allowedRoles={ADMIN_ROLES}
                    requiredPermission="view_candidates"
                  >
                    <CandidateList key="others" registrationType="Others" />
                  </PrivateRoute>
                }
              />
              <Route
                path="/candidates/all"
                element={
                  <PrivateRoute
                    allowedRoles={ADMIN_ROLES}
                    requiredPermission="view_candidates"
                  >
                    <AllCandidateList />
                  </PrivateRoute>
                }
              />

              <Route
                path="/candidates/add"
                element={
                  <PrivateRoute
                    allowedRoles={ADMIN_ROLES}
                    requiredPermission="create_candidate"
                  >
                    <AddCandidate />
                  </PrivateRoute>
                }
              />
              <Route
                path="/candidates/edit/:id"
                element={
                  <PrivateRoute
                    allowedRoles={ADMIN_ROLES}
                    requiredPermission="edit_candidate"
                  >
                    <EditCandidate />
                  </PrivateRoute>
                }
              />

              <Route
                path="/admin/role-permissions"
                element={
                  <PrivateRoute
                    allowedRoles={ADMIN_ROLES}
                    requiredPermission="manage_permissions"
                  >
                    <RolePermission />
                  </PrivateRoute>
                }
              />

              <Route
                path="/admin/log-history"
                element={
                  <PrivateRoute
                    allowedRoles={ADMIN_ROLES}
                    requiredPermission="view_logs"
                  >
                    <LogHistory />
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin/reimbursements"
                element={
                  <PrivateRoute allowedRoles={["SuperAdmin", "Admin", "admin"]}>
                    <ReimbursementAdminList />
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin/reimbursements/:id"
                element={
                  <PrivateRoute allowedRoles={["SuperAdmin", "Admin", "admin"]}>
                    <ReimbursementAdminDetails />
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin/notifications"
                element={
                  <PrivateRoute allowedRoles={["SuperAdmin", "Admin", "admin"]}>
                    <AdminNotifications />
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin/users"
                element={
                  <PrivateRoute
                    allowedRoles={ADMIN_ROLES}
                    requiredPermission="manage_admin_users"
                  >
                    <AdminUserList />
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin/admin-roles"
                element={
                  <PrivateRoute
                    allowedRoles={ADMIN_ROLES}
                    requiredPermission="manage_admin_roles"
                  >
                    <AdminRolesList />
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin/admin-roles/create"
                element={
                  <PrivateRoute
                    allowedRoles={ADMIN_ROLES}
                    requiredPermission="create_admin_role"
                  >
                    <AdminRolesForm />
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin/admin-roles/edit/:id"
                element={
                  <PrivateRoute
                    allowedRoles={ADMIN_ROLES}
                    requiredPermission="edit_admin_role"
                  >
                    <AdminRolesForm />
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin/users/create"
                element={
                  <PrivateRoute
                    allowedRoles={ADMIN_ROLES}
                    requiredPermission="create_admin_user"
                  >
                    <CreateAdminUser />
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin/users/edit/:id"
                element={
                  <PrivateRoute
                    allowedRoles={ADMIN_ROLES}
                    requiredPermission="edit_admin_user"
                  >
                    <EditAdminUser />
                  </PrivateRoute>
                }
              />

              <Route
                path="/hotel-details"
                element={
                  <PrivateRoute
                    allowedRoles={ADMIN_ROLES}
                    requiredPermission="view_hotels"
                  >
                    <HotelList />
                  </PrivateRoute>
                }
              />

              <Route
                path="/hotel-details/create"
                element={
                  <PrivateRoute allowedRoles={["SuperAdmin", "Admin", "admin"]}>
                    <CreateHotel />
                  </PrivateRoute>
                }
              />

              <Route
                path="/hotel-details/edit/:id"
                element={
                  <PrivateRoute allowedRoles={["SuperAdmin", "Admin", "admin"]}>
                    <EditHotel />
                  </PrivateRoute>
                }
              />

              <Route
                path="/system-manual"
                element={
                  <PrivateRoute allowedRoles={["SuperAdmin", "Admin", "admin"]}>
                    <SystemManualList />
                  </PrivateRoute>
                }
              />
              <Route
                path="/system-manual/create"
                element={
                  <PrivateRoute allowedRoles={["SuperAdmin", "Admin", "admin"]}>
                    <CreateSystemManual />
                  </PrivateRoute>
                }
              />
              <Route
                path="/system-manual/edit/:id"
                element={
                  <PrivateRoute allowedRoles={["SuperAdmin", "Admin", "admin"]}>
                    <EditSystemManual />
                  </PrivateRoute>
                }
              />
              <Route
                path="/system-manual-categories"
                element={
                  <PrivateRoute allowedRoles={["SuperAdmin", "Admin", "admin"]}>
                    <SystemManualCategoryList />
                  </PrivateRoute>
                }
              />

              {/* Study Material Routes */}
              <Route
                path="/study-material"
                element={
                  <PrivateRoute allowedRoles={["SuperAdmin", "Admin", "admin"]}>
                    <StudyMaterialList />
                  </PrivateRoute>
                }
              />
              <Route
                path="/study-material/add"
                element={
                  <PrivateRoute allowedRoles={["SuperAdmin", "Admin", "admin"]}>
                    <CreateStudyMaterial />
                  </PrivateRoute>
                }
              />
              <Route
                path="/study-material/edit/:id"
                element={
                  <PrivateRoute allowedRoles={["SuperAdmin", "Admin", "admin"]}>
                    <EditStudyMaterial />
                  </PrivateRoute>
                }
              />

              {/* Location Routes */}
              <Route
                path="/location"
                element={
                  <PrivateRoute
                    allowedRoles={ADMIN_ROLES}
                    requiredPermission="view_locations"
                  >
                    <LocationList />
                  </PrivateRoute>
                }
              />

              <Route
                path="/location/create"
                element={
                  <PrivateRoute
                    allowedRoles={ADMIN_ROLES}
                    requiredPermission="create_location"
                  >
                    <CreateLocation />
                  </PrivateRoute>
                }
              />

              <Route
                path="/location/edit/:id"
                element={
                  <PrivateRoute
                    allowedRoles={ADMIN_ROLES}
                    requiredPermission="edit_location"
                  >
                    <EditLocation />
                  </PrivateRoute>
                }
              />

              {/* Kevin - Nominator Routes */}
              <Route
                path="/nominators"
                element={
                  <PrivateRoute
                    allowedRoles={ADMIN_ROLES}
                    requiredPermission="view_nominators"
                  >
                    <NominatorList />
                  </PrivateRoute>
                }
              />
              <Route
                path="/nominators/add"
                element={
                  <PrivateRoute
                    allowedRoles={ADMIN_ROLES}
                    requiredPermission="create_nominator"
                  >
                    <CreateNominator />
                  </PrivateRoute>
                }
              />
              <Route
                path="/nominators/edit/:id"
                element={
                  <PrivateRoute
                    allowedRoles={ADMIN_ROLES}
                    requiredPermission="edit_nominator"
                  >
                    <EditNominator />
                  </PrivateRoute>
                }
              />

              {/* Feedback Routes */}
              <Route
                path="/feedback"
                element={
                  <PrivateRoute allowedRoles={["SuperAdmin", "Admin", "admin"]}>
                    <FeedbackCategoryList />
                  </PrivateRoute>
                }
              />
              <Route
                path="/feedback/forms"
                element={
                  <PrivateRoute allowedRoles={["SuperAdmin", "Admin", "admin"]}>
                    <FeedbackFormList />
                  </PrivateRoute>
                }
              />
              <Route
                path="/feedback/forms/create"
                element={
                  <PrivateRoute allowedRoles={["SuperAdmin", "Admin", "admin"]}>
                    <FeedbackFormCreate />
                  </PrivateRoute>
                }
              />
              <Route
                path="/feedback/forms/edit/:id"
                element={
                  <PrivateRoute allowedRoles={["SuperAdmin", "Admin", "admin"]}>
                    <FeedbackFormCreate />
                  </PrivateRoute>
                }
              />
              <Route
                path="/feedback/questions"
                element={
                  <PrivateRoute allowedRoles={["SuperAdmin", "Admin", "admin"]}>
                    <FeedbackQuestionList />
                  </PrivateRoute>
                }
              />
              <Route
                path="/feedback/submitted"
                element={
                  <PrivateRoute allowedRoles={["SuperAdmin", "Admin", "admin"]}>
                    <FeedbackCourseList />
                  </PrivateRoute>
                }
              />

              <Route
                path="/feedback/submitted/:candidateId/:activeCourseId"
                element={
                  <PrivateRoute allowedRoles={["SuperAdmin", "Admin", "admin"]}>
                    <SubmittedFeedbackDetails />
                  </PrivateRoute>
                }
              />

              {/* Assessment Routes */}
              <Route
                path="/assessment/question-bank"
                element={
                  <PrivateRoute allowedRoles={["SuperAdmin", "Admin", "admin"]}>
                    <QuestionBankList />
                  </PrivateRoute>
                }
              />
              <Route
                path="/assessment/question-bank/add"
                element={
                  <PrivateRoute allowedRoles={["SuperAdmin", "Admin", "admin"]}>
                    <QuestionBankForm />
                  </PrivateRoute>
                }
              />
              <Route
                path="/assessment/question-bank/edit/:id"
                element={
                  <PrivateRoute allowedRoles={["SuperAdmin", "Admin", "admin"]}>
                    <QuestionBankForm />
                  </PrivateRoute>
                }
              />

              {/* Assessment - Assessment List/Add/Edit Routes */}
              <Route
                path="/assessment/assessments"
                element={
                  <PrivateRoute allowedRoles={["SuperAdmin", "Admin", "admin"]}>
                    <AssessmentList />
                  </PrivateRoute>
                }
              />
              <Route
                path="/assessment/assessments/add"
                element={
                  <PrivateRoute allowedRoles={["SuperAdmin", "Admin", "admin"]}>
                    <AssessmentForm />
                  </PrivateRoute>
                }
              />
              <Route
                path="/assessment/assessments/edit/:id"
                element={
                  <PrivateRoute allowedRoles={["SuperAdmin", "Admin", "admin"]}>
                    <AssessmentForm />
                  </PrivateRoute>
                }
              />

              {/* Submitted Assessment Routes */}
              <Route
                path="/assessment/submitted"
                element={
                  <PrivateRoute allowedRoles={["SuperAdmin", "Admin", "admin"]}>
                    <SubmittedAssessmentList />
                  </PrivateRoute>
                }
              />
              <Route
                path="/assessment/submitted/:courseId"
                element={
                  <PrivateRoute allowedRoles={["SuperAdmin", "Admin", "admin"]}>
                    <CourseSubmissions />
                  </PrivateRoute>
                }
              />
              <Route
                path="/assessment/submission/:resultId"
                element={
                  <PrivateRoute allowedRoles={["SuperAdmin", "Admin", "admin"]}>
                    <SubmissionDetail />
                  </PrivateRoute>
                }
              />

              <Route
                path="/certificates"
                element={
                  <PrivateRoute allowedRoles={["SuperAdmin", "Admin", "admin"]}>
                    <CertificateList />
                  </PrivateRoute>
                }
              />
              <Route
                path="/certificates/create"
                element={
                  <PrivateRoute allowedRoles={["SuperAdmin", "Admin", "admin"]}>
                    <CreateCertificate />
                  </PrivateRoute>
                }
              />
              <Route
                path="/certificates/edit/:id"
                element={
                  <PrivateRoute allowedRoles={["SuperAdmin", "Admin", "admin"]}>
                    <EditCertificate />
                  </PrivateRoute>
                }
              />
              <Route
                path="/certificates/print/:id"
                element={
                  <PrivateRoute
                    allowedRoles={[
                      "SuperAdmin",
                      "Admin",
                      "admin",
                      "Candidate",
                      "candidate",
                    ]}
                    noLayout={true}
                  >
                    <CertificatePrintView />
                  </PrivateRoute>
                }
              />

              <Route
                path="/reports"
                element={
                  <PrivateRoute allowedRoles={["SuperAdmin", "Admin", "admin"]}>
                    <ReportDashboard />
                  </PrivateRoute>
                }
              />
              <Route
                path="/reports/hotel"
                element={
                  <PrivateRoute allowedRoles={["SuperAdmin", "Admin", "admin"]}>
                    <HotelReport />
                  </PrivateRoute>
                }
              />
              <Route
                path="/reports/admin-remarks"
                element={
                  <PrivateRoute
                    allowedRoles={["SuperAdmin", "Admin", "admin"]}
                    requiredPermission="view_admin_remarks"
                  >
                    <AdminRemarksReport />
                  </PrivateRoute>
                }
              />

              {/* Trainer Routes */}
              <Route
                path="/dashboard/trainer"
                element={
                  <PrivateRoute allowedRoles={TRAINER_ROLES}>
                    <UnifiedDashboard />
                  </PrivateRoute>
                }
              />
              <Route
                path="/dashboard/candidate"
                element={
                  <PrivateRoute allowedRoles={CANDIDATE_ROLES}>
                    <UnifiedDashboard />
                  </PrivateRoute>
                }
              />
              <Route
                path="/trainer-certificates"
                element={
                  <PrivateRoute allowedRoles={TRAINER_ROLES}>
                    <TrainerCertificateList />
                  </PrivateRoute>
                }
              />
              <Route
                path="/my-courses"
                element={
                  <PrivateRoute allowedRoles={TRAINER_ROLES}>
                    <TrainerCourseList />
                  </PrivateRoute>
                }
              />
              <Route
                path="/my-courses/edit/:id"
                element={
                  <PrivateRoute allowedRoles={TRAINER_ROLES}>
                    <ActiveCourseForm />
                  </PrivateRoute>
                }
              />
              <Route
                path="/trainer-feedback"
                element={
                  <PrivateRoute allowedRoles={["Trainer", "trainer"]}>
                    <FeedbackCourseList />
                  </PrivateRoute>
                }
              />

              <Route
                path="/trainer-feedback/submitted/:candidateId/:activeCourseId"
                element={
                  <PrivateRoute allowedRoles={["Trainer", "trainer"]}>
                    <SubmittedFeedbackDetails />
                  </PrivateRoute>
                }
              />

              <Route
                path="/trainer-assessments"
                element={
                  <PrivateRoute allowedRoles={["Trainer", "trainer"]}>
                    <AssessmentList />
                  </PrivateRoute>
                }
              />
              <Route
                path="/trainer-assessments/add"
                element={
                  <PrivateRoute allowedRoles={["Trainer", "trainer"]}>
                    <AssessmentForm />
                  </PrivateRoute>
                }
              />
              <Route
                path="/trainer-assessments/edit/:id"
                element={
                  <PrivateRoute allowedRoles={["Trainer", "trainer"]}>
                    <AssessmentForm />
                  </PrivateRoute>
                }
              />

              <Route
                path="/candidate-nominations"
                element={
                  <PrivateRoute allowedRoles={CANDIDATE_ROLES}>
                    <CandidateNominations />
                  </PrivateRoute>
                }
              />
              <Route
                path="/candidate-courses"
                element={
                  <PrivateRoute allowedRoles={CANDIDATE_ROLES}>
                    <CandidateCourseList />
                  </PrivateRoute>
                }
              />

              <Route
                path="/candidate-course/:id"
                element={
                  <PrivateRoute allowedRoles={CANDIDATE_ROLES}>
                    <CandidateCourseDetails />
                  </PrivateRoute>
                }
              />
              <Route
                path="/candidate-certificates"
                element={
                  <PrivateRoute allowedRoles={CANDIDATE_ROLES}>
                    <CandidateCertificateList />
                  </PrivateRoute>
                }
              />
              <Route
                path="/reimbursements"
                element={
                  <PrivateRoute
                    allowedRoles={CANDIDATE_ROLES}
                    molmiCandidateOnly={true}
                  >
                    <ReimbursementList />
                  </PrivateRoute>
                }
              />
              <Route
                path="/reimbursements/create"
                element={
                  <PrivateRoute
                    allowedRoles={CANDIDATE_ROLES}
                    molmiCandidateOnly={true}
                  >
                    <ReimbursementForm />
                  </PrivateRoute>
                }
              />
              <Route
                path="/reimbursements/:id"
                element={
                  <PrivateRoute
                    allowedRoles={CANDIDATE_ROLES}
                    molmiCandidateOnly={true}
                  >
                    <ReimbursementDetails />
                  </PrivateRoute>
                }
              />
              <Route
                path="/reimbursements/:id/edit"
                element={
                  <PrivateRoute
                    allowedRoles={CANDIDATE_ROLES}
                    molmiCandidateOnly={true}
                  >
                    <ReimbursementForm />
                  </PrivateRoute>
                }
              />

              {/* Demo route removed - all pages require authentication */}

              <Route path="*" element={<Navigate to="/login" replace />} />
            </Routes>
          </Suspense>
        </AuthProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
